import React, { useEffect, useRef } from "react";
import { MdFormatListBulleted } from "react-icons/md";

const eventConfig = {
  input_message: {
    label: "Input Message",
    color: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
    dotColor: "bg-sky-500",
  },
  output_message: {
    label: "Output Message",
    color: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
    dotColor: "bg-teal-500",
  },
  llm_request: {
    label: "LLM Request",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    dotColor: "bg-blue-500",
  },
  llm_response: {
    label: "LLM Response",
    color: "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400",
    dotColor: "bg-blue-400",
  },
  tool_start: {
    label: "Tool Start",
    color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
    dotColor: "bg-green-500",
  },
  tool_end: {
    label: "Tool End",
    color: "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400",
    dotColor: "bg-green-400",
  },
  sub_agent_start: {
    label: "Sub-Agent Start",
    color: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
    dotColor: "bg-purple-500",
  },
  sub_agent_end: {
    label: "Sub-Agent End",
    color: "bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400",
    dotColor: "bg-purple-400",
  },
  interrupt_context_switch: {
    label: "Context Switch",
    color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
    dotColor: "bg-orange-500",
  },
  interrupt_slot_update: {
    label: "Slot Update",
    color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
    dotColor: "bg-yellow-500",
  },
  interrupt_out_of_context: {
    label: "Out of Context",
    color: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
    dotColor: "bg-rose-500",
  },
};

function formatTime(timestamp) {
  if (!timestamp) return "";
  const d = new Date(timestamp);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", fractionalSecondDigits: 1 });
}

function getEventSummary(event) {
  const { type, payload, model } = event;

  switch (type) {
    case "input_message": {
      const msg = payload?.content || payload?.message || payload?.text || "";
      return msg.length > 80 ? msg.slice(0, 80) + "..." : msg || "Input received";
    }
    case "output_message": {
      const msg = payload?.content || payload?.message || payload?.text || "";
      return msg.length > 80 ? msg.slice(0, 80) + "..." : msg || "Response sent";
    }
    case "llm_request":
      return model ? `Model: ${model}` : "Sending request to LLM";
    case "llm_response":
      if (payload?.tool_calls?.length) {
        return `Tool calls: ${payload.tool_calls.map((tc) => tc.function?.name).join(", ")}`;
      }
      if (payload?.content) {
        return payload.content.length > 80 ? payload.content.slice(0, 80) + "..." : payload.content;
      }
      return "Response received";
    case "tool_start":
      return `${payload?.toolName || "Unknown tool"}`;
    case "tool_end": {
      const hasError = payload?.output?.error;
      return `${payload?.toolName || "Unknown tool"} ${hasError ? "- Error" : "- Done"}`;
    }
    case "sub_agent_start":
      return `${payload?.subAgentName || "Unknown"}: ${payload?.task ? (payload.task.length > 60 ? payload.task.slice(0, 60) + "..." : payload.task) : ""}`;
    case "sub_agent_end":
      return `${payload?.subAgentName || "Unknown"} completed`;
    case "interrupt_context_switch":
      return `Switch to: ${payload?.targetWorkflow || "unknown"} (${payload?.status || ""})`;
    case "interrupt_slot_update": {
      const slots = payload?.slotsToUpdate ? Object.keys(payload.slotsToUpdate).join(", ") : "";
      return `Slots: ${slots} (${payload?.status || ""})`;
    }
    case "interrupt_out_of_context":
      return payload?.question ? (payload.question.length > 70 ? payload.question.slice(0, 70) + "..." : payload.question) : "Off-topic question";
    default:
      return type;
  }
}

function TraceEvent({ event, isLatest }) {
  const cfg = eventConfig[event.type] || eventConfig.llm_request;
  const label = event.payload?.toolType === "sub_agent" ? "Sub Agent"
    : event.payload?.toolType === "workflow" ? "Workflow" : cfg.label;
  return (
    <li className="relative flex gap-3">
      <div className="flex w-3 shrink-0 flex-col items-center" aria-hidden="true">
        <div className={`mt-5 h-2 w-2 shrink-0 rounded-full ${cfg.dotColor} ${isLatest ? "ring-4 ring-gray-200" : ""}`} />
        {!isLatest && <div className="mt-1 w-px flex-1 bg-gray-200" />}
      </div>
      <div className={`mb-3 min-w-0 flex-1 rounded-xl border bg-white p-3 shadow-sm ${isLatest ? "border-gray-400" : "border-gray-200"}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-md px-2 py-1 text-[10px] font-semibold ${cfg.color}`}>{label}</span>
          {isLatest && <span className="text-[10px] font-medium text-gray-500">Latest</span>}
        </div>
        <p className="mt-2 break-words text-xs leading-relaxed text-gray-700">{getEventSummary(event)}</p>
        <time className="mt-2 block text-[10px] tabular-nums text-gray-400">{formatTime(event.timestamp)}</time>
      </div>
    </li>
  );
}

export default function TraceView({ events }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [events.length]);

  if (events.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
        <span className="mb-4 rounded-xl border border-gray-200 bg-white p-3 text-gray-400"><MdFormatListBulleted size={24} aria-hidden="true" /></span>
        <p className="text-sm font-medium text-gray-700">No events yet</p>
        <p className="mt-2 max-w-[220px] text-xs leading-relaxed text-gray-500">Messages, tool calls, and updates will appear as you test your agent.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-4 py-4 agent-monitor-scrollbar">
      <ol aria-label="Execution events">
      {events.map((event, i) => (
        <TraceEvent key={event.eventId || i} event={event} isLatest={i === events.length - 1} />
      ))}
      </ol>
      <div ref={bottomRef} />
    </div>
  );
}
